import type { Locale } from '@sentra/contracts';
// **Re-exported rather than reimplemented.** The server needs the same arithmetic to bound an
// export by a civil date, and two spellings of "what instant is this wall clock" is exactly the
// pair that drifts. `@sentra/contracts` reaches for no Node built-in, so `Intl` is the platform in
// both programs.
export { instantAtZone } from '@sentra/contracts';

/**
 * Dates, times, numbers and lists, formatted through `Intl` in the installation's reporting
 * timezone.
 *
 * **The timezone is an argument, never the browser's.** Every figure this product shows is about
 * one building: a movement at the gate, an occupancy count at an instant, a night's archive. A
 * terminal whose clock is set to another zone — a laptop brought from elsewhere, a container with
 * no `TZ` — would otherwise render the same event at two different times on two screens in the same
 * room, and the audit trail would be quoted wrongly in both. `REPORTING_TIMEZONE` comes from
 * `GET /api/v1/config` and reaches this module through the configuration provider, which is its
 * only source in the browser.
 *
 * **Every formatter is built once per (language, timezone) pair.** `Intl.DateTimeFormat` is
 * expensive to construct and cheap to reuse, and a table rendering five thousand rows constructs
 * one per cell if nobody arranges otherwise.
 *
 * **Each language is formatted under an explicit REGION.** `Intl` with a bare `en` formats dates
 * the American way — `Jan 1, 2026` — and this product is one building in Spain whose English exists
 * for the staff and visitors who do not read Spanish. Two people comparing two screens in the same
 * room must not see the same movement written in two orders, so `en` renders as `en-GB` and `es` as
 * `es-ES`. Naming the region also stops the platform's own default for a bare language tag from
 * moving the product's output on an ICU upgrade.
 *
 * **A timezone this platform does not know is refused loudly.** `Intl.DateTimeFormat` throws a
 * `RangeError` for an unknown zone, so {@link isSupportedTimeZone} is what the configuration
 * provider asks before it lets the application render: a misconfigured installation gets one
 * actionable screen rather than a crash inside whichever component happened to format a date first.
 */

/** Anything this module accepts as an instant: an ISO string from the API, or a `Date`. */
type Instant = string | Date;

export interface Formatters {
  /** The language these formatters were built for. */
  readonly locale: Locale;
  /** The IANA zone every date and time below is rendered in. */
  readonly timeZone: string;
  /** `11 sept 2026` in Spanish, `11 Sept 2026` in English. */
  readonly date: (value: Instant) => string;
  /** The date and the time to the minute, which is the resolution an operator reads. */
  readonly dateTime: (value: Instant) => string;
  /** The time alone, to the minute. */
  readonly time: (value: Instant) => string;
  /** The time to the second, for a movement log where two scans can share a minute. */
  readonly timeWithSeconds: (value: Instant) => string;
  /** A count: grouped, never with a decimal. */
  readonly integer: (value: number) => string;
  /** A measured quantity, to at most one decimal. */
  readonly decimal: (value: number) => string;
  /** `a, b y c` in Spanish, `a, b and c` in English. */
  readonly list: (values: readonly string[]) => string;
  /**
   * The CALENDAR date an instant falls on in the building's zone, as `YYYY-MM-DD`.
   *
   * Not a rendering: it is the value a date control is bounded by and the `today` the contract's own
   * date validators are given. It has to be the building's date rather than the browser's, because a
   * terminal whose clock is an hour behind would call a date of birth entered just after midnight
   * "in the future" — and it has to be machine-shaped rather than localised, because it is compared
   * against stored `YYYY-MM-DD` strings with `<` and `>`.
   */
  readonly calendarDate: (value: Instant) => string;
}

function instantOf(value: Instant): Date {
  return value instanceof Date ? value : new Date(value);
}

/** The region each of the product's two languages is formatted under. See the note above. */
const INTL_LOCALE: Readonly<Record<Locale, string>> = { es: 'es-ES', en: 'en-GB' };

/** Whether this platform's ICU knows a zone, asked before anything tries to format with it. */
export function isSupportedTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone });
    return true;
  } catch {
    return false;
  }
}

/** The formatters for one language in one zone. */
export function createFormatters(locale: Locale, timeZone: string): Formatters {
  const tag = INTL_LOCALE[locale];
  const date = new Intl.DateTimeFormat(tag, {
    timeZone,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const dateTime = new Intl.DateTimeFormat(tag, {
    timeZone,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const time = new Intl.DateTimeFormat(tag, {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const timeWithSeconds = new Intl.DateTimeFormat(tag, {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  // Assembled from parts rather than from a locale that happens to print ISO order: the output is
  // compared against stored dates with `<`, so it must be `YYYY-MM-DD` on every platform's ICU.
  const calendarParts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const integer = new Intl.NumberFormat(tag, { maximumFractionDigits: 0 });
  const decimal = new Intl.NumberFormat(tag, { maximumFractionDigits: 1 });
  const list = new Intl.ListFormat(tag, { style: 'long', type: 'conjunction' });

  return {
    locale,
    timeZone,
    date: (value) => date.format(instantOf(value)),
    dateTime: (value) => dateTime.format(instantOf(value)),
    time: (value) => time.format(instantOf(value)),
    timeWithSeconds: (value) => timeWithSeconds.format(instantOf(value)),
    integer: (value) => integer.format(value),
    decimal: (value) => decimal.format(value),
    list: (values) => list.format(values),
    calendarDate: (value) => {
      const parts = new Map(
        calendarParts.formatToParts(instantOf(value)).map((part) => [part.type, part.value]),
      );
      return `${parts.get('year') ?? ''}-${parts.get('month') ?? ''}-${parts.get('day') ?? ''}`;
    },
  };
}
